/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Grid } from '@mui/material';
// Other libraries
import { isEmpty } from 'lodash';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { FormProvider } from '../hook-form';
// Components
import ClinCheckDatePicker from './ClinCheckDatePicker';
import UserSearchDropdownSingleSelect from '../UserSearchDropdownSingleSelect';
// Redux
import { updateError } from '../../redux/slices/user';
import { dispatch, useSelector } from '../../redux/store';
import { getCurrentTreatmentIdV2, handleClinCheckInProgressStatus, handleClinCheckSentStatus } from '../../redux/slices/clincheck';
// Validations/utils/hooks
import { ClinCheckSentFormSchema } from '../../validations/commonValidations';
import { getUserDropdownValue, getUserId, practiceUserData } from '../../utils/clinCheckUtils';
import { useAuth } from '../../hooks';
// Styles
import { appointmentGridStyle } from '../../styles/DialogueTabs';

const ClinCheckSentForm = forwardRef(({ handleClose, currentTab, leadDetail }, ref) => {
    const { clinCheckListing } = useSelector((state) => state.clinCheck);
    const { followLead } = useSelector((state) => state.lead);
    const { practiceUser } = useSelector((state) => state.practiceUser);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { user } = useAuth();

    const defaultValues = {
        date_sent: null,
        who_sent: '',
        is_task_done: false
    };

    const currentUser = practiceUser.results?.find((e) => e?.user?.id === user?.pk);

    useEffect(() => {
        if (!isEmpty(practiceUserData(practiceUser)?.filter((ele) => ele?.user?.id === currentUser?.user?.id))) {
            setValue('who_sent', getUserDropdownValue(currentUser), { shouldValidate: true });
        }
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            async submit() {
                await handleSubmit((data) => onSubmit(data))();
            },
        }),
        []
    );

    const methods = useForm({
        resolver: yupResolver(ClinCheckSentFormSchema),
        defaultValues,
    });

    const {
        control,
        setValue,
        reset,
        handleSubmit,
    } = methods;

    const onSubmit = (values) => {
        reset();
        if (clinCheckListing?.results?.[0]?.status === 'SENT') {
            handleClose('finish');
            return;
        }
        if (isEmpty(currentTab)) {
            dispatch(updateError({
                message: 'Please select tab',
                status: false
            }));
            return;
        }
        if (!isEmpty(clinCheckListing?.results)) {
            handleUpdateStatus({ clinCheckId: clinCheckListing?.results?.[0]?.id, payload: values });
        } else {
            const data = {
                leadId: followLead?.id || leadDetail?.lead_id || leadDetail?.id,
                treatmentId: getCurrentTreatmentIdV2(followLead?.lead_treatments?.[0]?.id || leadDetail?.treatment_id || leadDetail?.lead_treatments?.[0]?.id),
                practiceId,
                payload: values
            };
            dispatch(handleClinCheckInProgressStatus(data, handleUpdateStatus));
        }
    };

    const handleUpdateStatus = (payload) => {
        const data = {
            leadId: followLead?.id || leadDetail?.lead_id || leadDetail?.id,
            treatmentId: getCurrentTreatmentIdV2(followLead?.lead_treatments?.[0]?.id || leadDetail?.treatment_id || leadDetail?.lead_treatments?.[0]?.id),
            clinCheckId: payload?.clinCheckId,
            practiceId,
            payload: {
                recorded_at: moment(payload?.payload?.date_sent).format('YYYY-MM-DDTHH:mm:ss.sssZ'),
                recorded_by: getUserId(practiceUser, payload?.payload?.who_sent),
                note: ''
            }
        };
        dispatch(handleClinCheckSentStatus(data, handleClose));
    };

    const handlePracticeUserChange = (e, newValue) => {
        if (newValue === null) {
            setValue('who_sent', '', { shouldValidate: true });
            return;
        };
        setValue('who_sent', newValue, { shouldValidate: true });
    };

    return <div>
        <FormProvider methods={methods}>
            <Grid sx={appointmentGridStyle}>
                <ClinCheckDatePicker name={'date_sent'} label={'Date sent'} control={control} />
                <UserSearchDropdownSingleSelect name={'who_sent'} control={control} onChange={handlePracticeUserChange} label='Who sent' />
            </Grid>
        </FormProvider>
    </div>;
});

export default ClinCheckSentForm;

ClinCheckSentForm.propTypes = {
    handleClose: PropTypes.func,
    currentTab: PropTypes.string,
    leadDetail: PropTypes.object,
};